import {
  Broadcast,
  CalendarCheck,
  ChartBar,
  ChatCircleDots,
  ClipboardText,
  Door,
  FileText,
  GameController,
  GoogleLogo,
  Link as LinkIcon,
  Receipt,
  Robot,
  Signature,
  Sparkle,
  Ticket,
  UsersThree,
  Wallet,
  VideoCamera,
} from "@phosphor-icons/react";

// ============================================================================
// Feature-discovery catalog — Hebrew copy, target route and the live
// suppressor for each nudge. The TRIGGER for each key lives server-side in
// functions/nudgeRules.js; this file is only what the therapist sees.
//
// Copy is deliberately one short sentence with no explanation: a therapist who
// wants the details asks Lenny.
//
// `suppress(state)` is the anti-staleness guard. Eligibility is computed
// nightly, so anything the client can verify right now — clinic doc flags are
// live via the clinic listener — is re-checked here and hides the tip the
// moment the feature is actually turned on.
//
// Keys must stay in sync with functions/nudgeRules.js; the parity test in
// functions/test/nudgeRules.test.js enforces it. An unknown key is skipped.
// ============================================================================
export const NUDGE_CATALOG = {
  lennySupport: {
    title:
      "ידעתם שיש לכם עוזרת AI שיכולה להדריך לגבי כל שאלה של שימוש במערכת ?",
    body: "לני עונה על כל שאלה על קליניקיט, בצ'אט, מתי שבא לכם.",
    route: "/dashboard/support",
    Icon: ChatCircleDots,
  },
  googleCalendarSync: {
    title: "ידעתם שאפשר לסנכרן את היומן עם Google?",
    body: "הפגישות מקליניקיט יופיעו ביומן האישי שלכם לצד כל השאר.",
    route: "/dashboard/calendar",
    Icon: GoogleLogo,
    suppress: ({ therapistProfile }) =>
      therapistProfile?.googleCalendarSync?.isConnected === true,
  },
  smsPatientReminders: {
    title: "ידעתם שאפשר לשלוח תזכורת SMS לפני כל פגישה?",
    body: "המערכת שולחת למטופלים תזכורת אוטומטית — פחות ביטולים ברגע האחרון.",
    route: "/dashboard/calendar",
    Icon: CalendarCheck,
  },
  digitalSignature: {
    title: "ידעתם שאפשר להחתים מטופלים דיגיטלית?",
    body: "שולחים טופס בקישור, המטופל חותם מהנייד — והמסמך נשמר בתיק.",
    route: "/dashboard/tasks?tab=forms",
    Icon: Signature,
  },
  financeActions: {
    title: "ידעתם שאפשר לעקוב אחרי תשלומים בקלות?",
    body: "לוח פיננסי עם סטטוס לכל פגישה — מי שילם, מי ממתין ומה צפוי.",
    route: "/dashboard/finance",
    Icon: Wallet,
  },
  questionnaires: {
    title: "ידעתם שיש שאלונים דיגיטליים למטופלים?",
    body: "שולחים שאלון בקישור, והתשובות מגיעות ישר לתיק המטופל.",
    route: "/dashboard/tasks?tab=questionnaires",
    Icon: ClipboardText,
  },
  selfBookingLink: {
    title: "ידעתם שמטופלים יכולים לקבוע תור בעצמם?",
    body: "קישור אישי שמראה רק את השעות הפנויות שלכם.",
    route: "/dashboard/calendar",
    Icon: LinkIcon,
    suppress: ({ clinicProfile }) =>
      clinicProfile?.bookingSettings?.enabled === true,
  },
  liveSession: {
    title: "ידעתם שיש מפגש חי עם תיעוד בזמן אמת?",
    body: "מתעדים תוך כדי הטיפול, והסיכום נכנס ישר לתיק המטופל.",
    route: "/dashboard/calendar",
    Icon: Broadcast,
  },
  monthlyReports: {
    title: "ידעתם שמחכה לכם דוח חודשי אוטומטי?",
    body: "סיכום של פגישות, הכנסות ומטופלים — מוכן בכל תחילת חודש.",
    route: "/dashboard/reports",
    Icon: ChartBar,
  },
  aiTreatmentChat: {
    title: "ידעתם שיש AI בתוך דוח הטיפול?",
    body: "שואלים אותו על התיק ומקבלים ניסוח לסיכום, בזמן הכתיבה.",
    route: "/dashboard/patients",
    Icon: Robot,
  },
  rooms: {
    title: "ידעתם שאפשר לנהל חדרים ביומן?",
    body: "משבצים פגישות לחדרים בלי התנגשויות — הכול ביומן אחד.",
    route: "/dashboard/admin/settings",
    Icon: Door,
    suppress: ({ clinicProfile }) => clinicProfile?.isSoloClinic === true,
  },
  punchCards: {
    title: "ידעתם שאפשר לנהל כרטיסיות למטופלים?",
    body: "מטופל משלם מראש על סדרת פגישות, והמערכת סופרת במקומכם.",
    route: "/dashboard/admin/settings",
    Icon: Ticket,
    suppress: ({ clinicProfile }) => clinicProfile?.punchCardsEnabled === true,
  },
  aiTreatmentsSummaryReport: {
    title: "ידעתם שאפשר להפיק סיכום טיפולים חכם?",
    body: "ה-AI מרכז את כל הטיפולים של מטופל לדוח אחד להורים או לגורם מפנה — מלשונית הטיפולים בכרטיס המטופל.",
    route: "/dashboard/patients",
    Icon: Sparkle,
  },

  // --- NiV-manual only: no rule in functions/nudgeRules.js, shown solely when
  // an admin sets visible[key] = true for a clinic. ---
  games: {
    title: "ידעתם שיש משחקים טיפוליים במפגש חי?",
    body: "משחקים אינטראקטיביים שאפשר לשלב בתוך המפגש.",
    route: "/dashboard/calendar",
    Icon: GameController,
  },
  morningInvoice: {
    title: "ידעתם שאפשר להפיק חשבוניות אוטומטית?",
    body: "חיבור ל־Morning מפיק חשבונית על כל תשלום — בלי הקלדות כפולות.",
    route: "/dashboard/admin/settings",
    Icon: Receipt,
  },
  digitalInvoice: {
    title: "ידעתם שאפשר להפיק חשבוניות דיגיטליות?",
    body: "מפיקים חשבונית ושולחים למטופל בקליק, ישירות מהמערכת.",
    route: "/dashboard/admin/settings",
    Icon: FileText,
  },
  videoMeetings: {
    title: "ידעתם שאפשר לקיים פגישות וידאו במערכת?",
    body: "שולחים למטופל קישור, והפגישה מתקיימת בתוך קליניקיט.",
    route: "/dashboard/admin/settings",
    Icon: VideoCamera,
  },
  // Multi-clinic v2: roles & permissions discovery for existing multi-therapist
  // clinics. The catalog has no client-side TRIGGER shape (eligibility is the
  // nightly server rule / NiV `visible` map), so the condition "clinic has no
  // multiClinicSettings AND is not solo AND the therapist doc has no
  // permissionsSchemaVersion" is expressed as the inverse suppressor: the tip
  // hides the moment any of those stops holding. Server-side it is a
  // manual-only key until a rule is added to functions/nudgeRules.js.
  teamRolesPermissions: {
    title: "חדש: תפקידים והרשאות לכל איש צוות",
    body: "הגדירו תפקידים, הרשאות אישיות ומצב שיתוף מטופלים בעמוד צוות הקליניקה.",
    route: "/dashboard/admin/team",
    Icon: UsersThree,
    suppress: ({ clinicProfile, therapistProfile }) =>
      clinicProfile?.isSoloClinic === true ||
      clinicProfile?.multiClinicSettings != null ||
      // Clinic-level stamp written by the auto-upgrade and by the first
      // updateTherapistRole call — hides the tip for every admin at once.
      clinicProfile?.permissionsSchemaVersion != null ||
      therapistProfile?.permissionsSchemaVersion != null ||
      !(
        therapistProfile?.isAdmin === true || therapistProfile?.role === "owner"
      ),
  },
};

/** True when a nudge should be hidden right now despite server eligibility. */
export const isNudgeSuppressed = (featureKey, state) => {
  const suppress = NUDGE_CATALOG[featureKey]?.suppress;
  if (typeof suppress !== "function") return false;
  try {
    return suppress(state) === true;
  } catch {
    return false;
  }
};
